import { type ErrorEvent } from '@sentry/react';

import { ROVO_EXTENSION_IDS } from '../constants/extensionIds';

/**
 * Drops errors that originate from *other* browser extensions injected into the
 * same page as the Rovo web app, rather than from our own code.
 *
 * Why these reach our project at all: the Sentry browser SDK instruments
 * `addEventListener`, wrapping every listener callback in a try/catch so it can
 * report unhandled exceptions. When a third-party extension registers a listener
 * on the shared page `window` (e.g. a `blur` handler) and that listener throws,
 * Sentry's wrapper catches the exception on our page and attributes it to the
 * Rovo project even though the failing code lives in `chrome-extension://<other>/…`.
 *
 * These are not Rovo bugs and cannot be fixed by us, so we drop them in
 * `beforeSend`. Example: ROVO-EXTENSION-FRONTEND-6J2 —
 * `ReferenceError: Cannot access 'w' before initialization` thrown from
 * `chrome-extension://bbidpgoneibefablhfcnaennjkfbflmk/content-injected/index.iife.js`.
 *
 * Safety: we only drop an event when it has at least one `chrome-extension://`
 * frame AND *every* attributable script frame is a `chrome-extension://` frame
 * that is not the Rovo extension. Genuine Rovo errors (which run in the iframe on
 * `https://…atlassian.com`) and normal page errors therefore always pass through.
 */

// `ROVO_EXTENSION_IDS` (our own dev/staging/prod IDs) is imported from
// `../constants/extensionIds`. Errors from those IDs are our own and must NOT be
// dropped by this filter, so they are used as an allow-list below. The frontend
// runs in an iframe served over https and would not normally produce
// `chrome-extension://<rovo-id>` frames here, but the allow-list ensures our own
// code is never accidentally silenced.

const CHROME_EXTENSION_FRAME = /^chrome-extension:\/\/([a-p]{32})\//;

/**
 * Frames whose filename matches these are SDK/runtime plumbing, not the code
 * that actually threw, so they must be ignored when deciding attribution.
 * Sentry's own wrapper (`@sentry/browser` helpers) is always on top of these
 * cross-extension stacks and would otherwise mask the real origin.
 */
const isNonAttributableFrame = (filename: string | undefined): boolean => {
	if (!filename) {
		return true;
	}
	if (filename === '<anonymous>' || filename === '[native code]') {
		return true;
	}
	// Sentry SDK plumbing (the instrumenting wrapper), regardless of how it is bundled.
	if (/@sentry[/\\]/.test(filename) || /\bsentry[^/\\]*\.(?:js|mjs|cjs)/.test(filename)) {
		return true;
	}
	return false;
};

const getExtensionId = (filename: string | undefined): string | undefined => {
	if (!filename) {
		return undefined;
	}
	const match = CHROME_EXTENSION_FRAME.exec(filename);
	return match?.[1];
};

/**
 * Returns `true` when the event should be dropped because it was thrown entirely
 * within a non-Rovo browser extension. See the module doc comment for rationale.
 */
export const isForeignExtensionError = (event: ErrorEvent): boolean => {
	const values = event.exception?.values;
	if (!values || values.length === 0) {
		return false;
	}

	let sawForeignExtensionFrame = false;

	for (const value of values) {
		const frames = value.stacktrace?.frames;
		if (!frames || frames.length === 0) {
			continue;
		}

		for (const frame of frames) {
			const filename = frame.filename;

			if (isNonAttributableFrame(filename)) {
				continue;
			}

			const extensionId = getExtensionId(filename);
			if (extensionId === undefined) {
				// An attributable, non-extension frame (e.g. our https app code or a
				// normal page script): this is not a pure foreign-extension error.
				return false;
			}

			if (ROVO_EXTENSION_IDS.includes(extensionId)) {
				// A frame from our own extension: never drop.
				return false;
			}

			sawForeignExtensionFrame = true;
		}
	}

	return sawForeignExtensionFrame;
};
